/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useState, useEffect } from 'react';
import {
  SectorProductContainer,
  States,
  FormInputName,
} from '@/features/registerEvent/screens/SectorProduct/ui';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import EventGroupSubgroup from '@/model/EventGroupSubgroup';
import api from '@/services/api';
import { AxiosError } from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDialog } from '@/hooks/useDialog';
import Event from '@/model/Event';
import ProductGroup from '@/model/ProductGroup';
import { SectionPos } from '@/model/SectionPos';
import EventPhaseCompletion from '@/model/EventPhaseCompletion';
import { ProducerLink, ProducerSectionLink } from '@/model/ProducerLink';
import { unmask as unmaskCash } from '@/helpers/masks/cashNumber';
import { FormInputNameProducerLink, formSectorProductProps, ShouldShowModal } from './types';
import { useEvent } from '../../hook/useEvent';
import { controllerEventProps } from '../SectorTicket/types';
import { FormRadioInput } from '../../components/CloneProduct';

type UrlParams = {
  id: string;
};

export interface SectorProductProps {
  phaseCompletion: EventPhaseCompletion | undefined;
  handleGetEventPhaseCompletion(): Promise<void>;
}

export const SectorProductScreen: React.FC<SectorProductProps> = ({
  phaseCompletion,
  handleGetEventPhaseCompletion,
}): JSX.Element => {
  const [state, setState] = useState<States>(States.default);
  const [groupOptions, setGroupOptions] = useState<ProductGroup[]>([]);
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(
    ShouldShowModal.cloneProduct,
  );
  const [eventChild, setEventChild] = useState<Event[]>([]);
  const [producerLink, setProducerLink] = useState<ProducerLink>();
  const [producerLinks, setProducerLinks] = useState<ProducerSectionLink[]>([]);
  const { title, visible, onChangeTitle, onToggle } = useDialog();

  const params = useParams<UrlParams>();
  const { eventState, onChange: onChangeEvent } = useEvent();

  const {
    formData: formDataSectorProduct,
    formErrors: formErrorsSectorProduct,
    isFormValid: isFormValidSectorProduct,
    onChangeFormInput: onChangeFormInputSectorProduct,
  } = useForm({
    initialData: {
      isProduct: '',
    },
    validators: {
      isProduct: [validators.required],
    },
    formatters: {},
  });

  const {
    formData: formDataCloneProduct,
    formErrors: formErrorsCloneProduct,
    onChangeFormInput: onChangeFormInputCloneProduct,
    isFormValid: isFormValidCloneProduct,
    resetForm: resetFormCloneProduct,
  } = useForm({
    initialData: {
      eventClone: '',
    },
    validators: {
      eventClone: [validators.required],
    },
  });

  const getEventData = async (eventId: string): Promise<void> => {
    const { data } = await api.get<boolean>(`/event/section-product/${eventId}/has`);
    onChangeFormInputSectorProduct(FormInputName.isProduct)(String(data));
  };

  const handleGetFirstStep = async (id: string): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<EventGroupSubgroup[]>(`/event/section-product/${id}/group`);

      if (data || []) {
        if (data && data.length > 0) {
          onChangeFormInputSectorProduct('isProduct')('true');
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };
  const handleAdvanceToPdv = async (): Promise<void> => {
    try {
      setState(States.loading);
      if (formDataSectorProduct[FormInputName.isProduct] !== 'false') {
        const { data } = await api.get<SectionPos[]>(`/event/section-product/${params.id}/pos`);
        if (!data || data.length === 0) {
          toast.error('Preencha todas as etapas para avançar para o próximo passo');
          return;
        }
      }
      onChangeEvent({ ...eventState, currentStep: eventState.currentStep + 1 });
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleGetGroupList = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get(`event/section-product/${params.id}/group`);

      setGroupOptions(data ?? []);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleHasProduct = async (b: string): Promise<void> => {
    try {
      setState(States.loading);
      await api.patch(`/event/section-product/${params.id}/has/${b}`);
      handleGetEventPhaseCompletion();
    } finally {
      setState(States.default);
    }
  };

  const controllerFormSectorProduct: formSectorProductProps = {
    formData: formDataSectorProduct,
    formErrors: formErrorsSectorProduct,
    onChangeFormInput: onChangeFormInputSectorProduct,
    isFormValidSectorProduct,
  };

  const controllerEvent: controllerEventProps = {
    eventState,
    onChangeEvent,
    groupOptions,
    handleGetGroupList,
  };

  const handleOnShowModal = ({
    value,
    newTitleModal,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
  }): void => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    if (value !== ShouldShowModal.copyProducerLink) {
      onToggle();
    }
  };

  const handListEvent = async (): Promise<void> => {
    try {
      setState(States.loading);
      handleOnShowModal({
        value: ShouldShowModal.cloneProduct,
        newTitleModal: 'Clonar produtos de outro evento',
      });
      const { data } = await api.get<Event[]>('/event/find');
      setEventChild(data ?? []);
    } finally {
      setState(States.default);
    }
  };

  const handleFetchAll = async (): Promise<void> => {
    getEventData(params.id);
    handleGetFirstStep(params.id);
    handleGetGroupList();
  };

  const handlOnCloneProducts = async (): Promise<void> => {
    if (isFormValidCloneProduct()) {
      try {
        setState(States.loading);
        onToggle();
        const oldEventId = formDataCloneProduct[FormRadioInput.eventClone];
        await api.post(`/event/copy-product/${params.id}/${oldEventId}`);
        await handleFetchAll();
        resetFormCloneProduct();
      } catch (error) {
        const err = error as AxiosError;
        toast.error(err.message);
      } finally {
        setState(States.default);
      }
    }
  };

  const {
    formData: formDataProducerProductLink,
    formErrors: formErrorsProducerProductLink,
    onChangeFormInput: onChangeFormInputProducerProductLink,
    isFormValid: isFormValidProducerProductLink,
    resetForm: resetFormProducerProductLink,
  } = useForm({
    initialData: {
      allowClone: 'false',
      sectionName: '',
      commission: '0,00',
    },
    validators: {
      allowClone: [validators.required],
      sectionName: [validators.required],
      commission: [validators.required],
    },
  });

  const handleOnOpenProducerLinkModal = (): void => {
    handleOnShowModal({
      value: ShouldShowModal.producerLink,
      newTitleModal: 'Gerar link para produtor',
    });
  };

  const handleGetProducerLinks = async (): Promise<void> => {
    try {
      const { data } = await api.get<ProducerSectionLink[]>(
        `/event/producer/product-links/${params.id}`,
      );
      setProducerLinks(data ?? []);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    }
  };

  const handleOnOpenProducerLinksModal = (): void => {
    handleOnShowModal({
      value: ShouldShowModal.producerLinks,
      newTitleModal: 'Links do evento',
    });
  };

  const handleOnGenerateProducerLink = async (): Promise<void> => {
    if (isFormValidProducerProductLink()) {
      try {
        setState(States.loading);
        const payload = {
          eventId: params.id,
          allowClone: formDataProducerProductLink[FormInputNameProducerLink.allowClone] === 'true',
          sectionName: formDataProducerProductLink[FormInputNameProducerLink.sectionName],
          commission: Number(
            unmaskCash(formDataProducerProductLink[FormInputNameProducerLink.commission]),
          ),
        };
        const { data } = await api.post<ProducerLink>(`/event/producer/product-link`, payload);
        setProducerLink(data);
        await handleGetProducerLinks();
        resetFormProducerProductLink();
        handleOnShowModal({
          value: ShouldShowModal.copyProducerLink,
          newTitleModal: 'Link para produtor',
        });
      } catch (error) {
        const err = error as AxiosError;
        toast.error(err.message);
      } finally {
        setState(States.default);
      }
    }
  };

  const handleOnCopyLink = async (url: string): Promise<void> => {
    await navigator.clipboard.writeText(url);
    toast.success('Link copiado com sucesso!');
  };

  useEffect(() => {
    handleFetchAll();
    handleGetProducerLinks();
  }, []);

  return (
    <SectorProductContainer
      onAdvanceToPdv={handleAdvanceToPdv}
      cloneProducts={handListEvent}
      controllerEvent={controllerEvent}
      formSectorProduct={controllerFormSectorProduct}
      phaseCompletion={phaseCompletion}
      state={state}
      onHandleHasProduct={handleHasProduct}
      handleGetEventPhaseCompletion={handleGetEventPhaseCompletion}
      title={title}
      visible={visible}
      shouldShowModal={shouldShowModal}
      onCloneProducts={handlOnCloneProducts}
      onToggle={onToggle}
      eventChild={eventChild}
      formDataCloneProduct={formDataCloneProduct}
      formErrorsCloneProduct={formErrorsCloneProduct}
      onChangeFormCloneProduct={onChangeFormInputCloneProduct}
      openProducerLinkModal={handleOnOpenProducerLinkModal}
      openProducerLinksModal={handleOnOpenProducerLinksModal}
      formDataProducerProductLink={formDataProducerProductLink}
      formErrorsProducerProductLink={formErrorsProducerProductLink}
      onChangeFormProducerProductLink={onChangeFormInputProducerProductLink}
      onGenerateProducerLink={handleOnGenerateProducerLink}
      producerLink={producerLink}
      producerLinks={producerLinks}
      onCopyLink={handleOnCopyLink}
    />
  );
};
